import usLocations from "@/data/us-locations.json";
import caLocations from "@/data/ca-locations.json";

export interface USLocation {
  city: string;
  state_name: string;
  zip: string;
  country: "US" | "CA";
}

export interface LocationOption {
  value: string;
  label: string;
  city: string;
  state: string;
  zip: string;
  country: "US" | "CA";
}

const PROVINCE_BY_CODE: Record<string, string> = {
  ab: "alberta",
  bc: "british columbia",
  mb: "manitoba",
  nb: "new brunswick",
  nl: "newfoundland and labrador",
  ns: "nova scotia",
  nt: "northwest territories",
  nu: "nunavut",
  on: "ontario",
  pe: "prince edward island",
  qc: "quebec",
  sk: "saskatchewan",
  yt: "yukon",
};

type CaRow = { city: string; state_name: string; zip: string };

export function foldLocationText(value: string): string {
  return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
}

function compactPostal(value: string): string {
  return value.replace(/\s+/g, "").toUpperCase();
}

function isCanadianPostalQuery(term: string): boolean {
  return /^[A-Z]\d[A-Z](\d[A-Z]\d)?$/.test(compactPostal(term));
}

let allLocations: USLocation[] | null = null;

export function getAllLocations(): USLocation[] {
  if (allLocations) return allLocations;
  const us = (usLocations.Sheet1 || []).map((location) => ({
    city: location.city,
    state_name: location.state_name,
    zip: location.zip,
    country: "US" as const,
  }));
  const ca = (caLocations as CaRow[]).map((location) => ({
    city: location.city,
    state_name: location.state_name,
    zip: location.zip,
    country: "CA" as const,
  }));
  allLocations = [...us, ...ca];
  return allLocations;
}

function cityStateLabel(location: USLocation): string {
  if (location.country === "CA") return `${location.city}, ${location.state_name}, Canada`;
  return `${location.city}, ${location.state_name}`;
}

function postalLabel(location: USLocation): string {
  if (location.country === "CA") return `${cityStateLabel(location)} ${location.zip}`;
  return `${location.city}, ${location.state_name} ${location.zip}`;
}

function toOption(location: USLocation, label: string): LocationOption {
  return {
    value: label,
    label,
    city: location.city,
    state: location.state_name,
    zip: location.zip,
    country: location.country,
  };
}

/** Keep US-only queries in file order, and leave room for Canada when both match. */
export function takeBalancedByCountry<T extends { country?: "US" | "CA" }>(
  matches: T[],
  limit: number,
): T[] {
  const us = matches.filter((match) => match.country !== "CA");
  const ca = matches.filter((match) => match.country === "CA");
  if (ca.length === 0) return us.slice(0, limit);
  if (us.length === 0) return ca.slice(0, limit);
  const caFloor = Math.min(ca.length, Math.floor(limit / 2));
  const usFloor = Math.min(us.length, limit - caFloor);
  const caExtra = Math.min(ca.length - caFloor, limit - usFloor - caFloor);
  return [...us.slice(0, usFloor), ...ca.slice(0, caFloor + caExtra)];
}

function matchesLocation(location: USLocation, query: string): boolean {
  const searchTerm = foldLocationText(query.trim());
  if (searchTerm.length < 2) return false;

  const city = foldLocationText(location.city);
  const state = foldLocationText(location.state_name);
  const zip = location.zip.toLowerCase();
  const provinceName = PROVINCE_BY_CODE[searchTerm];
  if (provinceName) return state === provinceName;

  if (isCanadianPostalQuery(query)) {
    if (location.country !== "CA") return false;
    const compact = compactPostal(query);
    const fsa = compactPostal(location.zip);
    return fsa.startsWith(compact) || compact.startsWith(fsa);
  }

  if (/^\d{2,5}$/.test(searchTerm)) return location.zip.startsWith(searchTerm);

  return (
    city.includes(searchTerm) ||
    state.includes(searchTerm) ||
    zip.startsWith(searchTerm)
  );
}

// Search locations by city or state
export function searchLocations(query: string, limit = 10): LocationOption[] {
  if (!query || query.trim().length < 2) return [];

  const seen = new Set<string>();
  const matches: USLocation[] = [];
  for (const location of getAllLocations()) {
    if (!matchesLocation(location, query)) continue;
    const key = `${location.country}|${location.city}|${location.state_name}`;
    if (seen.has(key)) continue;
    seen.add(key);
    matches.push(location);
  }

  return takeBalancedByCountry(matches, limit).map((location) =>
    toOption(location, cityStateLabel(location)),
  );
}

/** City, state, and ZIP or Canadian postal-code suggestions. Each code stays its own row. */
export function searchCityStateZip(query: string, limit = 8): LocationOption[] {
  if (!query || query.trim().length < 2) return [];

  const seen = new Set<string>();
  const matches: USLocation[] = [];
  for (const location of getAllLocations()) {
    if (!matchesLocation(location, query)) continue;
    const value = postalLabel(location);
    if (seen.has(value)) continue;
    seen.add(value);
    matches.push(location);
  }

  return takeBalancedByCountry(matches, limit).map((location) =>
    toOption(location, postalLabel(location)),
  );
}

// Get unique states
export function getStates(): string[] {
  const locations = getAllLocations();
  const states = [...new Set(locations.map((loc) => loc.state_name))];
  return states.sort();
}

// Get cities by state
export function getCitiesByState(state: string): string[] {
  const locations = getAllLocations();
  const cities = locations
    .filter((loc) => loc.state_name === state)
    .map((loc) => loc.city);
  return [...new Set(cities)].sort();
}
